import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { useSession } from '@/contexts/SessionContext';
import { useInitData } from '@/contexts/InitDataContext';
import type { CurrentStaff, UserRole } from '@/features/auth/types';
import type { TrustedAdminRole } from '@/features/auth/staffIdentity';
import { updateTrustedStaffSession } from '@/features/auth/trustedStaffSession';

export type StaffIdentityStatus = 'idle' | 'resolving' | 'allowed' | 'denied';

interface AuthContextValue {
  user: CurrentStaff;
  /** Роль для интерфейса. */
  role: UserRole;
  /** Доверенная роль персонала из /init — от неё считаются права. */
  trustedRole: TrustedAdminRole | null;
  identityStatus: StaffIdentityStatus;
  canEnterAdminShell: boolean;
  isSuperAdmin: boolean;
  permissionsVersion: number;
  refreshPermissions: () => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

const NO_ACCESS_USER: CurrentStaff = {
  id: '',
  name: '',
  role: 'client',
};

interface AuthProviderProps {
  children: ReactNode;
}

/**
 * Личность и права кабинета.
 *
 * Единственный источник — `me.roles` из `GET /init`: `ceo`, `manager` или `support`.
 * Нет роли персонала — доступа в кабинет нет; сервер такого пользователя и сам
 * не пустит, но интерфейс не должен на это полагаться.
 */
export function AuthProvider({ children }: AuthProviderProps) {
  const { isAuthReady, isAuthenticated } = useSession();
  const { status: initStatus, data: initData, reload: reloadInit } = useInitData();

  const [permissionsVersion, setPermissionsVersion] = useState(0);

  const trustedRole = initData?.identity.trustedRole ?? null;
  const isSuperAdmin = trustedRole === 'ceo';

  const identityStatus: StaffIdentityStatus = useMemo(() => {
    if (!isAuthReady) return 'resolving';
    if (!isAuthenticated) return 'idle';
    if (initStatus === 'loading' || initStatus === 'idle') return 'resolving';
    if (initStatus === 'error') return 'denied';
    return trustedRole ? 'allowed' : 'denied';
  }, [isAuthReady, isAuthenticated, initStatus, trustedRole]);

  const canEnterAdminShell = identityStatus === 'allowed';

  // Доверенная личность для проверки прав: отсюда её берут гварды.
  useEffect(() => {
    updateTrustedStaffSession({
      uid: initData?.identity.id ?? null,
      allowed: canEnterAdminShell,
      role: trustedRole,
    });
  }, [initData?.identity.id, canEnterAdminShell, trustedRole]);

  const effectiveRole: UserRole = useMemo(() => {
    if (!canEnterAdminShell || !initData) return 'client';
    return initData.identity.role;
  }, [canEnterAdminShell, initData]);

  const user: CurrentStaff = useMemo(() => {
    if (!initData) return NO_ACCESS_USER;
    return {
      id: initData.identity.id,
      name: initData.identity.name,
      role: effectiveRole,
    };
  }, [initData, effectiveRole]);

  const refreshPermissions = useCallback(() => {
    reloadInit();
    setPermissionsVersion((version) => version + 1);
  }, [reloadInit]);

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      role: effectiveRole,
      trustedRole,
      identityStatus,
      canEnterAdminShell,
      isSuperAdmin,
      permissionsVersion,
      refreshPermissions,
    }),
    [
      user,
      effectiveRole,
      trustedRole,
      identityStatus,
      canEnterAdminShell,
      isSuperAdmin,
      permissionsVersion,
      refreshPermissions,
    ],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
}
